/**
 * Creator Performance Service
 * Calculates dynamic CPV rates based on creator Instagram metrics
 * 
 * Formula:
 * CPV Rate = Base Rate × Performance Score
 * 
 * Performance Score = 
 *   (Engagement Rate × 0.4) +
 *   (Avg Views Per Post × 0.3) +
 *   (Normalized Followers × 0.2) +
 *   (Reach Rate × 0.1)
 */

interface CreatorMetrics {
  followers: number;
  totalPosts: number;
  totalLikes: number;
  totalComments: number;
  totalViews: number;
  totalReach: number;
  avgEngagementRate?: number;
}

interface PerformanceScore {
  engagementRate: number;
  avgViewsPerPost: number;
  normalizedFollowers: number;
  reachRate: number;
  totalScore: number;
  performanceMultiplier: number;
  tier: 'mega' | 'macro' | 'mid' | 'micro' | 'nano';
}

interface CreatorCPVRate {
  creatorId: string;
  baseCPV: number;
  performanceScore: PerformanceScore;
  adjustedCPV: number;
  appliedAt: Date;
}

export class CreatorPerformanceService {
  /**
   * Calculate engagement rate
   * Engagement Rate = (Likes + Comments) / Followers
   */
  private static calculateEngagementRate(metrics: CreatorMetrics): number {
    if (metrics.followers === 0) return 0;
    const totalEngagement = metrics.totalLikes + metrics.totalComments;
    return (totalEngagement / metrics.followers) * 100; // percentage
  }

  /**
   * Calculate average views per post
   */
  private static calculateAvgViewsPerPost(metrics: CreatorMetrics): number {
    if (metrics.totalPosts === 0) return 0;
    return metrics.totalViews / metrics.totalPosts;
  }

  /**
   * Normalize followers (0-1 scale)
   * Using logarithmic scale to account for platform growth
   */
  private static normalizeFollowers(followers: number): number {
    if (followers === 0) return 0;
    // Log scale: 10K = 0.5, 100K = 0.75, 1M = 1.0
    const logFollowers = Math.log10(followers + 1);
    const maxLog = Math.log10(1000000 + 1); // 1M followers = max
    return Math.min(logFollowers / maxLog, 1);
  }

  /**
   * Calculate reach rate
   * Reach Rate = Total Reach / (Followers × Total Posts)
   */
  private static calculateReachRate(metrics: CreatorMetrics): number {
    if (metrics.followers === 0 || metrics.totalPosts === 0) return 0;
    const maxPossibleReach = metrics.followers * metrics.totalPosts;
    return (metrics.totalReach / maxPossibleReach) * 100; // percentage
  }

  /**
   * Calculate performance score (0-1 scale)
   * Weighted formula:
   * - Engagement Rate: 40% (shows audience quality)
   * - Avg Views Per Post: 30% (shows viral potential)
   * - Followers: 20% (shows reach potential)
   * - Reach Rate: 10% (shows actual reach efficiency)
   */
  static calculatePerformanceScore(metrics: CreatorMetrics): PerformanceScore {
    const engagementRate = Math.min(this.calculateEngagementRate(metrics) / 10, 1); // Cap at 10% engagement
    const avgViewsPerPost = Math.min(this.calculateAvgViewsPerPost(metrics) / 100000, 1); // Cap at 100K views
    const normalizedFollowers = this.normalizeFollowers(metrics.followers);
    const reachRate = Math.min(this.calculateReachRate(metrics) / 100, 1); // Cap at 100% reach

    const totalScore =
      engagementRate * 0.4 +
      avgViewsPerPost * 0.3 +
      normalizedFollowers * 0.2 +
      reachRate * 0.1;

    const performanceMultiplier = Math.max(totalScore, 0.2); // Minimum 0.2x multiplier (ensures fair minimum pay)

    return {
      engagementRate: parseFloat((engagementRate * 100).toFixed(2)),
      avgViewsPerPost: parseFloat(this.calculateAvgViewsPerPost(metrics).toFixed(0)),
      normalizedFollowers: parseFloat((normalizedFollowers * 100).toFixed(2)),
      reachRate: parseFloat((reachRate * 100).toFixed(2)),
      totalScore: parseFloat((totalScore * 100).toFixed(2)),
      performanceMultiplier: parseFloat(performanceMultiplier.toFixed(3)),
      tier: this.calculateTier(performanceMultiplier)
    };
  }

  /**
   * Calculate creator tier based on performance multiplier
   */
  private static calculateTier(multiplier: number): 'mega' | 'macro' | 'mid' | 'micro' | 'nano' {
    if (multiplier >= 0.85) return 'mega';
    if (multiplier >= 0.65) return 'macro';
    if (multiplier >= 0.45) return 'mid';
    if (multiplier >= 0.3) return 'micro';
    return 'nano';
  }

  /**
   * Calculate adjusted CPV rate for a creator
   * Adjusted CPV = Base CPV × Performance Multiplier
   */
  static calculateAdjustedCPV(baseCPV: number, metrics: CreatorMetrics): CreatorCPVRate {
    const performanceScore = this.calculatePerformanceScore(metrics);
    const adjustedCPV = baseCPV * performanceScore.performanceMultiplier;

    return {
      creatorId: '', // Will be set by caller
      baseCPV,
      performanceScore,
      adjustedCPV: parseFloat(adjustedCPV.toFixed(4)),
      appliedAt: new Date()
    };
  }

  /**
   * Get recommended base CPV based on campaign budget and reach
   */
  static getRecommendedBaseCPV(budgetInr: number, targetReachViews: number): number {
    if (targetReachViews === 0) return 0.05; // Default
    return budgetInr / targetReachViews;
  }

  /**
   * Generate performance summary for dashboard
   */
  static generatePerformanceSummary(
    baseCPV: number,
    metrics: CreatorMetrics,
    projectedViews: number
  ): {
    baseCPV: number;
    adjustedCPV: number;
    performanceScore: PerformanceScore;
    projectedEarnings: number;
    earningsBoost: number;
    earningsBoostPercent: number;
  } {
    const rate = this.calculateAdjustedCPV(baseCPV, metrics);
    const baseEarnings = baseCPV * projectedViews;
    const adjustedEarnings = rate.adjustedCPV * projectedViews;
    const boost = adjustedEarnings - baseEarnings;
    const boostPercent = (boost / baseEarnings) * 100;

    return {
      baseCPV: parseFloat(baseCPV.toFixed(4)),
      adjustedCPV: rate.adjustedCPV,
      performanceScore: rate.performanceScore,
      projectedEarnings: parseFloat(adjustedEarnings.toFixed(2)),
      earningsBoost: parseFloat(boost.toFixed(2)),
      earningsBoostPercent: parseFloat(boostPercent.toFixed(1))
    };
  }
}

export default CreatorPerformanceService;
